/** Graficas SVG puras del dashboard movil, sin librerias nuevas (design.md §22.7). */

type ChartLegendItem = {
  label: string;
  color: string;
};

type BarSeriesPoint = {
  label: string;
  primary: number;
  accent: number;
};

type BarSeriesChartProps = {
  title: string;
  legend: ChartLegendItem[];
  series: BarSeriesPoint[];
};

type DonutSegment = {
  label: string;
  value: number;
  color: string;
};

type DonutChartProps = {
  title: string;
  centerValue: string;
  segments: DonutSegment[];
};

/** Ningun texto de las graficas baja de 10px (design.md §22.7). */
const MIN_TEXT_SIZE = 10;

const BAR_VIEW_WIDTH = 320;
const BAR_VIEW_HEIGHT = 190;
const BAR_PAD_X = 10;
const BAR_PAD_TOP = 12;
const BAR_PAD_BOTTOM = 26;
const BAR_PLOT_HEIGHT = BAR_VIEW_HEIGHT - BAR_PAD_TOP - BAR_PAD_BOTTOM;
const BAR_WIDTH = 12;
const BAR_GAP = 3;
const BAR_RADIUS = 3;
const GUIDE_FRACTIONS = [0, 0.5, 1] as const;

const DONUT_SIZE = 160;
const DONUT_CENTER = DONUT_SIZE / 2;
const DONUT_RADIUS = 56;
const DONUT_STROKE = 20;
const DONUT_CIRCUMFERENCE = 2 * Math.PI * DONUT_RADIUS;

/** Barras agrupadas (primary + accent) por dia, con leyenda y eje minimo. */
export function BarSeriesChart({ title, legend, series }: BarSeriesChartProps) {
  const maxValue = Math.max(1, ...series.flatMap((point) => [point.primary, point.accent]));
  const innerWidth = BAR_VIEW_WIDTH - BAR_PAD_X * 2;
  const groupWidth = series.length > 0 ? innerWidth / series.length : innerWidth;
  const primaryColor = legend[0]?.color ?? 'var(--color-primary)';
  const accentColor = legend[1]?.color ?? 'var(--color-accent)';

  function barHeight(value: number): number {
    return (Math.max(0, value) / maxValue) * BAR_PLOT_HEIGHT;
  }

  function barY(value: number): number {
    return BAR_PAD_TOP + BAR_PLOT_HEIGHT - barHeight(value);
  }

  return (
    <div className="w-full">
      <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1">
        {legend.map((item) => (
          <span key={item.label} className="flex items-center gap-1.5 text-xs text-text-secondary">
            <span
              aria-hidden="true"
              className="h-2.5 w-2.5 shrink-0 rounded-full"
              style={{ backgroundColor: item.color }}
            />
            {item.label}
          </span>
        ))}
      </div>

      <svg
        viewBox={`0 0 ${BAR_VIEW_WIDTH} ${BAR_VIEW_HEIGHT}`}
        role="img"
        aria-label={`Gráfica de barras: ${title}`}
        className="h-auto w-full"
      >
        {GUIDE_FRACTIONS.map((fraction) => (
          <line
            key={fraction}
            x1={BAR_PAD_X}
            x2={BAR_VIEW_WIDTH - BAR_PAD_X}
            y1={BAR_PAD_TOP + BAR_PLOT_HEIGHT * (1 - fraction)}
            y2={BAR_PAD_TOP + BAR_PLOT_HEIGHT * (1 - fraction)}
            strokeWidth={1}
            className="stroke-border-light"
          />
        ))}

        {series.map((point, index) => {
          const center = BAR_PAD_X + groupWidth * (index + 0.5);
          return (
            <g key={point.label}>
              <rect
                x={center - BAR_WIDTH - BAR_GAP / 2}
                y={barY(point.primary)}
                width={BAR_WIDTH}
                height={barHeight(point.primary)}
                rx={BAR_RADIUS}
                style={{ fill: primaryColor }}
              />
              <rect
                x={center + BAR_GAP / 2}
                y={barY(point.accent)}
                width={BAR_WIDTH}
                height={barHeight(point.accent)}
                rx={BAR_RADIUS}
                style={{ fill: accentColor }}
              />
              <text
                x={center}
                y={BAR_VIEW_HEIGHT - 8}
                textAnchor="middle"
                fontSize={MIN_TEXT_SIZE}
                className="fill-text-secondary"
              >
                {point.label}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

/** Dona con stroke-dasharray, total centrado y leyenda con valor y porcentaje. */
export function DonutChart({ title, centerValue, segments }: DonutChartProps) {
  const total = segments.reduce((sum, segment) => sum + Math.max(0, segment.value), 0);
  const safeTotal = total > 0 ? total : 1;
  let consumed = 0;
  const arcs = segments.map((segment) => {
    const length = (Math.max(0, segment.value) / safeTotal) * DONUT_CIRCUMFERENCE;
    const arc = { segment, length, offset: consumed };
    consumed += length;
    return arc;
  });

  return (
    <div className="w-full">
      <div className="mx-auto w-full max-w-[220px]">
        <svg
          viewBox={`0 0 ${DONUT_SIZE} ${DONUT_SIZE}`}
          role="img"
          aria-label={`Gráfica de dona: ${title}, total ${centerValue}`}
          className="h-auto w-full"
        >
          <g transform={`rotate(-90 ${DONUT_CENTER} ${DONUT_CENTER})`}>
            {arcs.map(({ segment, length, offset }) => (
              <circle
                key={segment.label}
                cx={DONUT_CENTER}
                cy={DONUT_CENTER}
                r={DONUT_RADIUS}
                fill="none"
                strokeWidth={DONUT_STROKE}
                strokeDasharray={`${length} ${DONUT_CIRCUMFERENCE - length}`}
                strokeDashoffset={-offset}
                style={{ stroke: segment.color }}
              />
            ))}
          </g>
          <text
            x={DONUT_CENTER}
            y={DONUT_CENTER - 4}
            textAnchor="middle"
            fontSize={22}
            fontWeight={700}
            className="fill-text-heading tabular-nums"
          >
            {centerValue}
          </text>
          <text
            x={DONUT_CENTER}
            y={DONUT_CENTER + 14}
            textAnchor="middle"
            fontSize={MIN_TEXT_SIZE}
            className="fill-text-tertiary"
          >
            Total
          </text>
        </svg>
      </div>

      <ul className="mt-3 space-y-1.5">
        {segments.map((segment) => {
          const percent = Math.round((Math.max(0, segment.value) / safeTotal) * 100);
          return (
            <li key={segment.label} className="flex items-center gap-2 text-xs">
              <span
                aria-hidden="true"
                className="h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: segment.color }}
              />
              <span className="min-w-0 flex-1 truncate text-text-secondary">{segment.label}</span>
              <span className="shrink-0 font-medium tabular-nums text-text-primary">
                {segment.value} · {percent}%
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
